import { useEffect, useRef } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useProjectNavigation } from '@client/hooks/use-router.hooks';
import { URL_CATEGORY_NONE } from '@client/models/router.model';
import { Page } from '@client/store/ui/ui.model';
import { useSelectedOrganizationName } from '@client/store/organization';
import { isInputEvent } from '@shared/helpers/event.helpers';
import { NODON_PROJECTS_LIST_PATH } from '@shared/constants/router.constants';

const pageKeys = ['d', 'b', 'r'] as const;
const pageKeysSet = new Set<string>(pageKeys);
type PageKey = (typeof pageKeys)[number];
const isPageKey = (key: string): key is PageKey => pageKeysSet.has(key);

const KEY_TO_PAGE_MAP: Record<PageKey, Page> = {
  d: Page.Design,
  b: Page.Elements,
  r: Page.Report,
};

/**
 * Hotkeys for top-level navigation:
 *   D = Design, B = Build (Elements), R = Report, Shift+P = Project list
 * Pass only the D/B/R keys relevant for the current view; Shift+P is always active.
 */
export const usePageNavigationHotkeys = (
  enabledKeys: ReadonlyArray<PageKey>,
) => {
  const navigateTo = useProjectNavigation();
  const navigate = useNavigate();
  const organization = useSelectedOrganizationName();
  const ref = useRef({ navigateTo, navigate, organization, enabledKeys });
  ref.current = { navigateTo, navigate, organization, enabledKeys };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isInputEvent(e)) return;

      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key.toLowerCase();

      // Shift+P → project list (always, regardless of enabledKeys)
      if (e.shiftKey && key === 'p') {
        e.preventDefault();
        const org = ref.current.organization;
        if (org) {
          const path = generatePath(NODON_PROJECTS_LIST_PATH, {
            organization: org,
          });
          ref.current.navigate(path);
        }
        return;
      }
      if (e.shiftKey) return;

      if (!isPageKey(key) || !ref.current.enabledKeys.includes(key)) return;

      e.preventDefault();
      ref.current.navigateTo({
        page: KEY_TO_PAGE_MAP[key],
        elementId: URL_CATEGORY_NONE,
      });
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);
};
